import React, { useEffect, useState } from "react";
import Input from "../../components/input";
import FIlterSelect from "../../components/filter-select";
import Table from "../../components/table";
import {
  capitalizeFirstLetter,
  formatDate,
  getRoleColor,
  getRoleTextColor,
} from "../../utilz/function";
import EditUser from "./user-modal/edit-user";

interface Permissions {
  canViewReports: boolean;
  canManageUsers: boolean;
  canManageInventory: boolean;
  canManageSales: boolean;
  canManageSupport: boolean;
  [key: string]: boolean;
}

interface UserData {
  _id: string;
  email: string;
  invitedBy: string;
  isActive: string;
  permissions: Permissions;
  role: string;
  createdAt: string;
  username: string;
  lastLogin: string;
  whatsappNo: string;
  updatedAt: string;
  [key: string]: string | undefined | Permissions;
}

interface UserTableProps {
  data: UserData[];
  selectedStatus?: string;
}

const UserTable: React.FC<UserTableProps> = ({
  data,
  selectedStatus,
}) => {
  const [filteredData, setFilteredData] = useState<UserData[]>([]);
  const [editUserModal, setEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState("");

  const columns = [
    { header: "User", accessor: "contact" },
    { header: "Role", accessor: "role" },
    { header: "Permissions", accessor: "permissions" },
    { header: "Last Login", accessor: "lastLogin" },
    { header: "Status", accessor: "isActive" },
    { header: "Actions", accessor: "_id" },
  ];

  useEffect(() => {
    const filtered = data.filter((user) => {

      const statusMatch = !selectedStatus || user.isActive === selectedStatus;
    
      const searchMatch = 
        !searchQuery || 
        user.username?.toLowerCase().includes(searchQuery?.toLowerCase()) || 
        user.email?.toLowerCase().includes(searchQuery?.toLowerCase());
      const roleMatch = !selectedRole || user?.role === selectedRole;
      
      return statusMatch && searchMatch && roleMatch;
    });
    setFilteredData(filtered);
  }, [data, selectedStatus, searchQuery, selectedRole]);

  function closeEditUserModal() {
    setEditUserModal(false);
    setSelectedUser(null);
  }

  function openEditUserModal(id: string) {
    const userToEdit = data.find(user => user._id === id) || null;
    setSelectedUser(userToEdit);
    setEditUserModal(true);
  }

  const handleEditClick = (id: string) => {
    openEditUserModal(id);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleRoleFilterChange = (selectedValue: string) => {
    setSelectedRole(selectedValue);
  };

  const uniqueRoles = Array.from(new Set(data.map(user => user.role)));

  const combinedData = filteredData.map((user) => ({
    ...user,
    contact: (
      <div>
        <div>{user?.username || "N/A"}</div>
        <div>{user?.email || "N/A"}</div>
      </div>
    ),
    role: (
      <div>
        <span
          className="font-normal rounded-full p-2"
          style={{
            backgroundColor: getRoleColor(user?.role),
            color: getRoleTextColor(user?.role),
          }}
        >
          {user?.role || "N/A"}
        </span>
      </div>
    ),
    isActive: user?.isActive === "true" ? "Active" : "Inactive",
    lastLogin: `${formatDate(user?.lastLogin)}` || "N/A",
    permissions: user?.permissions
      ? Object.keys(user.permissions)
          .filter((key) => user.permissions[key])
          .length.toString()
      : "N/A",
  }));

  return (
    <>
      <div className="py-3 bg-white rounded-3xl shadow border border-[#C9C9C9] mt-10 pt-5">
        <div className="md:flex justify-between px-5">
          <p className="text-[20px] font-bold">Users</p>
          <div className="md:flex gap-2 items-center md:mt-0 mt-2">
            <Input
              name="search"
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full"
              placeholder="Search by name or email"
              icon="searchIcon"
            />

            <FIlterSelect
              name="roleFilter"
              options={[
                { value: "", label: "Roles" },
                ...uniqueRoles.map(role => ({ value: role, label: capitalizeFirstLetter(role) }))
              ]}
              className="mt-2 md:-mt-2 w-full text-[10px] md:w-[50%]"
              icon="filterIcon"
              onChange={handleRoleFilterChange}
              value={selectedRole}
            />
          </div>
        </div>

        {combinedData?.length > 0 ? (
          <Table
            columns={columns}
            data={combinedData}
            selectedUserId={selectedUser?._id || null}
            onEditClick={handleEditClick}
          />
        ) : (
          <div className="opacity-80 mt-10 font-bold flex justify-center mx-auto">
            No users found
          </div>
        )}
      </div>
      <EditUser
        isVisible={editUserModal}
        onClose={closeEditUserModal}
        userData={selectedUser}
      />
    </>
  );
};

export default UserTable;